import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Stack,
  useTheme,
  useMediaQuery,
  alpha,
} from '@mui/material';
import { ArrowForward as ArrowForwardIcon } from '@mui/icons-material';

interface HeroAction {
  label: string;
  onClick?: () => void;
  href?: string;
  variant?: 'contained' | 'outlined' | 'text';
  color?: 'primary' | 'secondary' | 'inherit';
  icon?: React.ReactNode;
}

interface HeroProps {
  title: string;
  subtitle?: string;
  description?: string;
  image?: string;
  video?: string;
  overlay?: boolean;
  overlayOpacity?: number;
  height?: 'small' | 'medium' | 'large' | 'full' | number;
  align?: 'left' | 'center' | 'right';
  actions?: HeroAction[];
  background?: string;
  backgroundGradient?: string;
  children?: React.ReactNode;
}

const heightMap = {
  small: 300,
  medium: 500,
  large: 700,
  full: '100vh',
};

/**
 * Hero component for creating impactful landing sections
 * Supports images, videos, gradients, and overlays
 */
export const Hero: React.FC<HeroProps> = ({
  title,
  subtitle,
  description,
  image,
  video,
  overlay = true,
  overlayOpacity = 0.5,
  height = 'medium',
  align = 'center',
  actions = [],
  background,
  backgroundGradient,
  children,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const getHeight = () => {
    if (typeof height === 'number') return height;
    return heightMap[height];
  };

  const getTextAlign = () => {
    if (align === 'center') return 'center';
    if (align === 'right') return 'right';
    return 'left';
  };

  const getAlignItems = () => {
    if (align === 'center') return 'center';
    if (align === 'right') return 'flex-end';
    return 'flex-start';
  };

  const renderBackground = () => {
    if (video) {
      return (
        <Box
          component="video"
          autoPlay
          muted
          loop
          playsInline
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
        >
          <source src={video} type="video/mp4" />
        </Box>
      );
    }

    if (image) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundImage: `url(${image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0,
          }}
        />
      );
    }

    if (backgroundGradient) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: backgroundGradient,
            zIndex: 0,
          }}
        />
      );
    }

    if (background) {
      return (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: background,
            zIndex: 0,
          }}
        />
      );
    }

    return null;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: getHeight(),
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',
        backgroundColor: background || theme.palette.background.default,
      }}
    >
      {renderBackground()}

      {overlay && (image || video) && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: alpha(theme.palette.common.black, overlayOpacity),
            zIndex: 1,
          }}
        />
      )}

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 2,
          py: isMobile ? 4 : 6,
        }}
      >
        <Box
          sx={{
            textAlign: getTextAlign(),
            maxWidth: align === 'center' ? '800px' : '600px',
            mx: align === 'center' ? 'auto' : 0,
          }}
        >
          {subtitle && (
            <Typography
              variant="overline"
              sx={{
                display: 'block',
                color: (image || video) ? 'common.white' : 'primary.main',
                fontWeight: 600,
                letterSpacing: 1.5,
                mb: 1,
                opacity: 0.9,
              }}
            >
              {subtitle}
            </Typography>
          )}

          <Typography
            variant={isMobile ? 'h3' : isTablet ? 'h2' : 'h1'}
            component="h1"
            sx={{
              fontWeight: 700,
              color: (image || video) ? 'common.white' : 'text.primary',
              mb: 2,
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {description && (
            <Typography
              variant={isMobile ? 'body1' : 'h6'}
              sx={{
                color: (image || video) ? 'grey.100' : 'text.secondary',
                mb: 4,
                fontWeight: 400,
                lineHeight: 1.6,
              }}
            >
              {description}
            </Typography>
          )}

          {actions.length > 0 && (
            <Stack
              direction={isMobile ? 'column' : 'row'}
              spacing={2}
              sx={{
                justifyContent: getAlignItems(),
                alignItems: isMobile ? 'stretch' : 'center',
              }}
            >
              {actions.map((action, index) => (
                <Button
                  key={index}
                  variant={action.variant || (index === 0 ? 'contained' : 'outlined')}
                  color={action.color || 'primary'}
                  size="large"
                  onClick={action.onClick}
                  href={action.href}
                  endIcon={action.icon || (index === 0 && <ArrowForwardIcon />)}
                  sx={{
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    ...(image || video
                      ? {
                          borderColor: 'rgba(255, 255, 255, 0.5)',
                          color: action.variant === 'outlined' ? 'white' : undefined,
                          '&:hover': {
                            borderColor: 'white',
                            backgroundColor:
                              action.variant === 'outlined'
                                ? 'rgba(255, 255, 255, 0.1)'
                                : undefined,
                          },
                        }
                      : {}),
                  }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          )}

          {children && (
            <Box sx={{ mt: 4 }}>
              {children}
            </Box>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default Hero;